import { apiClient } from "./client";
import { API_BASE_URL } from "../config/constants";

/**
 * Auth API Response Types
 */
export interface ChallengeResponse {
  message: string;
  nonce: string;
  blockhash?: string; // For Ledger fallback - server-issued blockhash
}

export interface VerifyResponse {
  token: string;
  wallet: string;
  expiresIn: string;
  jti: string;
  sid: string;
}

export interface LogoutResponse {
  success: boolean;
  message: string;
}

export interface VerifyTokenResponse {
  valid: boolean;
  wallet?: string;
  jti?: string;
  sid?: string;
}

/**
 * Transaction-based signature for Ledger fallback
 */
export interface TxSignaturePayload {
  signature: string; // Base58 encoded
  serializedTx: string; // Base64 encoded
  blockhash: string;
}

/**
 * Verify request payload
 */
export interface VerifyRequest {
  wallet: string;
  message: string;
  // Standard signature (Base58) - for normal wallets
  signature?: string;
  // Alternative: Transaction-based signature for Ledger fallback
  signedTransaction?: string; // Base64 encoded serialized transaction
}

/**
 * Auth API Client
 * Handles all authentication-related API calls
 */
export const authAPI = {
  /**
   * Get authentication challenge for a wallet
   * @param wallet - Solana wallet address
   * @returns Challenge message and nonce to sign
   */
  async getChallenge(wallet: string): Promise<ChallengeResponse> {
    return apiClient.get<ChallengeResponse>("/api/auth/challenge", { wallet });
  },

  /**
   * Verify signed message and get JWT token
   * Supports two signature types:
   * 1. Standard signature (Base58) - for normal wallets
   * 2. Transaction-based signature (Base64 serialized tx) - for Ledger fallback
   *
   * @param request - Verify request containing wallet, message, and signature
   * @returns JWT token and session info
   */
  async verify(request: VerifyRequest): Promise<VerifyResponse> {
    return apiClient.post<VerifyResponse>("/api/auth/verify", request);
  },

  /**
   * Verify if a JWT token is valid
   * @param token - JWT token to verify
   * @returns Token validity status
   */
  async verifyToken(token: string): Promise<VerifyTokenResponse> {
    const response = await fetch(`${API_BASE_URL}/api/auth/verify-token`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to verify token");
    }

    return response.json();
  },

  /**
   * Logout and invalidate current session
   * @param token - JWT token to invalidate
   */
  async logout(token: string): Promise<LogoutResponse> {
    const response = await fetch(`${API_BASE_URL}/api/auth/logout`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to logout");
    }

    return response.json();
  },
};
